/**
 * Homepage company showcase: two slowly scrolling rows of employer names.
 *
 * Names, not logos. The licensing review on 2026-10-21 found no logo set that
 * could be used here (several are absent from Simple Icons; Apple's and
 * Google's guidelines bar their logos from a third party's marketing), so the
 * names are set in the site's own typeface. They are examples of employers job
 * seekers aim for, and the disclaimer under them says so.
 *
 * How the motion stays accessible:
 *
 *   - Each row is one real <ul> followed by an identical copy marked
 *     `aria-hidden`, which is what lets the loop run without a seam. A screen
 *     reader hears each name once.
 *   - Movement pauses on hover, while a row has keyboard focus (each row is a
 *     focusable group, so a keyboard user can stop it to read), and from the
 *     Pause button, which WCAG 2.2.2 asks for on content that moves for more
 *     than five seconds. Focus on the button itself does not pause anything,
 *     or pressing Play could never resume the rows.
 *   - Under `prefers-reduced-motion` the rows do not move at all: the copy is
 *     removed and the names wrap as a static list (see `.marquee` in
 *     index.css). The Pause button is hidden there because nothing moves.
 *   - The animation is pure CSS, so the prerendered page scrolls before any
 *     JavaScript runs and needs no carousel library.
 *   - Nothing is a link. There is no destination on this site for a company.
 */

import { useState } from 'react'
import { Pause, Play } from 'lucide-react'

const GROUPS = [
  {
    id: 'product',
    label: 'Product and technology companies',
    names: ['Google', 'Microsoft', 'Amazon', 'Netflix', 'Meta', 'Apple', 'Adobe', 'Salesforce'],
  },
  {
    id: 'services',
    label: 'IT services and consulting companies',
    names: [
      'Tata Consultancy Services (TCS)',
      'Infosys',
      'Accenture',
      'Wipro',
      'Cognizant',
      'Capgemini',
      'HCLTech',
      'Tech Mahindra',
    ],
  },
] as const

const NameList = ({ names, copy = false }: { names: readonly string[]; copy?: boolean }) => (
  <ul className={`marquee-list${copy ? ' marquee-copy' : ''}`} aria-hidden={copy || undefined}>
    {names.map((name) => (
      <li
        key={name}
        className="shrink-0 whitespace-nowrap rounded-xl border border-base-300 bg-base-200/60 px-4 py-2.5 text-base font-semibold tracking-tight text-base-content/75 transition-[transform,border-color,color,background-color] duration-150 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-base-100 hover:text-base-content"
      >
        {name}
      </li>
    ))}
  </ul>
)

export const CompanyShowcase = ({ className = '' }: { className?: string }) => {
  const [paused, setPaused] = useState(false)

  return (
    <section
      aria-labelledby="employers-heading"
      className={`company-showcase ${className}`}
      data-paused={paused ? 'true' : undefined}
    >
      <div className="mx-auto max-w-2xl text-center">
        <h2 id="employers-heading" className="text-balance text-xl font-bold tracking-tight sm:text-2xl">
          Prepare your resume for the companies you&rsquo;re targeting
        </h2>
        <p className="mt-2 text-balance text-sm text-base-content/75 sm:text-base">
          Whichever employer you apply to, a recruiter needs to see your skills and experience
          quickly. The free tools and guides here help you present them clearly.
        </p>
      </div>

      <div className="mt-5 space-y-5">
        {GROUPS.map((group) => (
          <div key={group.id}>
            <h3
              id={`employers-${group.id}`}
              className="text-center text-xs font-semibold uppercase tracking-wide text-base-content/70"
            >
              {group.label}
            </h3>
            <div
              className="marquee mt-2.5 rounded-xl"
              data-reverse={group.id === 'services' ? 'true' : undefined}
              role="group"
              aria-labelledby={`employers-${group.id}`}
              tabIndex={0}
            >
              {/* The edge fade lives on this inner box, not on the focusable
                  row: a mask also hides the row's own focus ring. */}
              <div className="marquee-viewport rounded-xl">
                <div className="marquee-track py-1">
                  <NameList names={group.names} />
                  <NameList names={group.names} copy />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col items-center gap-2">
        <button
          type="button"
          className="btn btn-ghost btn-sm min-h-9 gap-1.5 motion-reduce:hidden"
          onClick={() => setPaused((value) => !value)}
        >
          {paused ? <Play size={14} aria-hidden="true" /> : <Pause size={14} aria-hidden="true" />}
          {paused ? 'Play company list' : 'Pause company list'}
        </button>
        <p className="max-w-2xl text-center text-xs text-base-content/70">
          Company names are examples of employers job seekers often apply to. ATSResumeKit is not
          affiliated with or endorsed by any of them, and no tool can promise how a particular
          employer&rsquo;s hiring system will handle your resume.
        </p>
      </div>
    </section>
  )
}
